import { formatDistanceToNow, isThisMonth, isThisWeek, isToday } from "date-fns";
import { es } from "date-fns/locale";
import { Activity, ChevronDown, ChevronRight, MonitorSmartphone, Wifi } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ConfirmModal from "../../../shared/components/ConfirmModal";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import { useDeleteRom, useRomParticipantsQuery, useRomsQuery } from "../hooks/useRoms";
import type { ParticipantRole, Rom, RomStatus, RomType } from "../types";

const getTypeBadge = (type: RomType) => {
  switch (type) {
    case "local":
      return <Badge className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30">Local</Badge>;
    case "remote":
      return (
        <Badge className="bg-purple-500/20 text-purple-400 hover:bg-purple-500/30">Remoto</Badge>
      );
    default:
      return null;
  }
};

const getStatusBadge = (status: RomStatus) => {
  switch (status) {
    case "active":
      return <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/30">Activo</Badge>;
    case "deleted":
      return <Badge className="bg-red-500/20 text-red-400 hover:bg-red-500/30">Eliminado</Badge>;
    default:
      return null;
  }
};

const getRoleBadge = (role: ParticipantRole) => {
  switch (role) {
    case "host":
      return <Badge className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30">Host</Badge>;
    case "guest":
      return <Badge className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30">Guest</Badge>;
    default:
      return null;
  }
};

const ParticipantsRow = ({ romId }: { romId: string }) => {
  const { data, isLoading } = useRomParticipantsQuery(romId);
  const participants = data?.data ?? [];

  if (isLoading) {
    return (
      <TableRow className="border-neutral-800 bg-neutral-950/50">
        <TableCell colSpan={8} className="py-3 pl-10">
          <span className="text-xs text-neutral-500">Cargando participantes...</span>
        </TableCell>
      </TableRow>
    );
  }

  if (participants.length === 0) {
    return (
      <TableRow className="border-neutral-800 bg-neutral-950/50">
        <TableCell colSpan={8} className="py-3 pl-10">
          <span className="text-xs text-neutral-500">Sin participantes registrados</span>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <TableRow className="border-neutral-800 bg-neutral-950/50 hover:bg-neutral-950/70">
        <TableCell colSpan={8} className="p-0">
          <div className="ml-6 border-l border-neutral-700 pl-4 py-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-neutral-500">
                  <th className="text-left py-1 pr-4 font-medium">Participante</th>
                  <th className="text-left py-1 pr-4 font-medium">Rol</th>
                  <th className="text-left py-1 pr-4 font-medium">Device ID</th>
                  <th className="text-left py-1 pr-4 font-medium">Plataforma</th>
                  <th className="text-left py-1 pr-4 font-medium">Ingreso</th>
                  <th className="text-left py-1 font-medium">Salida</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p) => (
                  <tr key={p.id} className="text-neutral-300">
                    <td className="py-1.5 pr-4 font-medium text-neutral-100">{p.label}</td>
                    <td className="py-1.5 pr-4">{getRoleBadge(p.role)}</td>
                    <td className="py-1.5 pr-4 font-mono text-xs text-neutral-400">
                      {p.deviceId.length > 20 ? `${p.deviceId.slice(0, 20)}...` : p.deviceId}
                    </td>
                    <td className="py-1.5 pr-4 text-neutral-400">{p.platform ?? "—"}</td>
                    <td className="py-1.5 pr-4 text-neutral-400">
                      {formatDistanceToNow(new Date(p.joinedAt), {
                        addSuffix: true,
                        locale: es,
                      })}
                    </td>
                    <td className="py-1.5 text-neutral-400">
                      {p.leftAt ? (
                        formatDistanceToNow(new Date(p.leftAt), {
                          addSuffix: true,
                          locale: es,
                        })
                      ) : (
                        <Badge className="bg-green-500/10 text-green-400 text-[10px]">
                          Conectado
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TableCell>
      </TableRow>
    </>
  );
};

type UsagePeriod = "day" | "week" | "month";

const computeTypeStats = (roms: Rom[]) => {
  const local = roms.filter((r) => r.type === "local").length;
  const remote = roms.filter((r) => r.type === "remote").length;
  const total = roms.length;
  const localPct = total > 0 ? Math.round((local / total) * 100) : 0;
  const remotePct = total > 0 ? 100 - localPct : 0;
  return { local, remote, total, localPct, remotePct };
};

const computeUsageByPeriod = (roms: Rom[], period: UsagePeriod) => {
  return roms.filter((r) => {
    const d = new Date(r.createdAt);
    switch (period) {
      case "day":
        return isToday(d);
      case "week":
        return isThisWeek(d, { weekStartsOn: 1 });
      case "month":
        return isThisMonth(d);
    }
  }).length;
};

const periodLabels: Record<UsagePeriod, string> = {
  day: "Hoy",
  week: "Esta semana",
  month: "Este mes",
};

const RomsPage = () => {
  const { data, isLoading, isFetching } = useRomsQuery();
  const deleteMutation = useDeleteRom();

  const [statusFilter, setStatusFilter] = useState<"all" | RomStatus>("all");
  const [expandedRomId, setExpandedRomId] = useState<string | null>(null);
  const [usagePeriod, setUsagePeriod] = useState<UsagePeriod>("week");
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; romId: string | null }>({
    isOpen: false,
    romId: null,
  });

  const roms = data?.data ?? [];
  const isBusy = isLoading || isFetching || deleteMutation.isPending;

  const filteredRoms = useMemo(() => {
    if (statusFilter === "all") return roms;
    return roms.filter((r) => r.status === statusFilter);
  }, [roms, statusFilter]);

  const typeStats = useMemo(() => computeTypeStats(roms), [roms]);
  const usageCount = useMemo(() => computeUsageByPeriod(roms, usagePeriod), [roms, usagePeriod]);
  const activeCount = useMemo(() => roms.filter((r) => r.status === "active").length, [roms]);

  const handleDelete = (romId: string) => {
    setDeleteModal({ isOpen: true, romId });
  };

  const confirmDelete = async () => {
    if (deleteModal.romId) {
      await deleteMutation.mutateAsync(deleteModal.romId);
    }
  };

  const toggleExpand = (romId: string) => {
    setExpandedRomId((prev) => (prev === romId ? null : romId));
  };

  const hasFilters = statusFilter !== "all";

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="ROMs"
        subtitle="Sesiones remotas de dispositivos conectados (Remote Online Meeting)"
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Card 1: Local vs Remote */}
        <Card className="border-neutral-800 bg-neutral-900/50 backdrop-blur-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="text-sm uppercase tracking-wide text-neutral-400">
                Tipo de conexión
              </div>
              <div className="text-purple-400">
                <Wifi className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3 flex items-end gap-3">
              <div className="text-3xl font-semibold text-white">
                {typeStats.localPct}
                <span className="text-lg text-neutral-400">%</span>
              </div>
              <div className="mb-1 text-sm text-neutral-400">local</div>
            </div>
            <div className="mt-3 w-full h-2 rounded-full bg-neutral-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
                style={{ width: `${typeStats.localPct}%` }}
              />
            </div>
            <div className="mt-2 flex justify-between text-xs text-neutral-500">
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
                Local: {typeStats.local}
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-purple-500" />
                Remoto: {typeStats.remote}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Usage by period */}
        <Card className="border-neutral-800 bg-neutral-900/50 backdrop-blur-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="text-sm uppercase tracking-wide text-neutral-400">
                Sesiones creadas
              </div>
              <div className="text-purple-400">
                <Activity className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3 text-3xl font-semibold text-white">
              {usageCount.toLocaleString("es-AR")}
            </div>
            <div className="mt-2 flex gap-1">
              {(["day", "week", "month"] as UsagePeriod[]).map((p) => (
                <button
                  key={p}
                  onClick={() => setUsagePeriod(p)}
                  className={`rounded px-2 py-0.5 text-xs transition-colors ${
                    usagePeriod === p
                      ? "bg-purple-500/20 text-purple-400"
                      : "bg-neutral-800 text-neutral-500 hover:text-neutral-300"
                  }`}
                >
                  {periodLabels[p]}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Active sessions */}
        <Card className="border-neutral-800 bg-neutral-900/50 backdrop-blur-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="text-sm uppercase tracking-wide text-neutral-400">
                Sesiones activas
              </div>
              <div className="text-purple-400">
                <MonitorSmartphone className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3 text-3xl font-semibold text-white">
              {activeCount.toLocaleString("es-AR")}
            </div>
            <div className="mt-1 text-sm text-neutral-400">
              de {roms.length} total{roms.length !== 1 ? "es" : ""}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-neutral-800 bg-neutral-900/50">
        <div className="border-b border-neutral-800 px-4 py-3">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-neutral-400">Estado:</Label>
              <Select
                value={statusFilter}
                onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
              >
                <SelectTrigger className="h-8 w-[140px] border-neutral-700 bg-neutral-800/50 text-xs text-neutral-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-neutral-700 bg-neutral-900">
                  <SelectItem value="all" className="text-neutral-200">
                    Todos
                  </SelectItem>
                  <SelectItem value="active" className="text-neutral-200">
                    Activos
                  </SelectItem>
                  <SelectItem value="deleted" className="text-neutral-200">
                    Eliminados
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStatusFilter("all")}
                className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
              >
                Limpiar filtros
              </Button>
            )}
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="border-neutral-800 hover:bg-transparent">
              <TableHead className="w-8 text-neutral-400" />
              <TableHead className="text-neutral-400">Label</TableHead>
              <TableHead className="text-neutral-400">Tipo</TableHead>
              <TableHead className="text-neutral-400">Device ID</TableHead>
              <TableHead className="text-neutral-400">Workspace</TableHead>
              <TableHead className="text-neutral-400">Estado</TableHead>
              <TableHead className="text-neutral-400">Creado</TableHead>
              <TableHead className="text-right text-neutral-400">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRoms.map((rom) => {
              const isExpanded = expandedRomId === rom.id;
              return (
                <>
                  <TableRow
                    key={rom.id}
                    className="border-neutral-800 hover:bg-neutral-800/50 cursor-pointer"
                    onClick={() => toggleExpand(rom.id)}
                  >
                    <TableCell className="w-8 pr-0">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-neutral-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-neutral-400" />
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-neutral-100">{rom.label}</span>
                    </TableCell>
                    <TableCell>{getTypeBadge(rom.type)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-neutral-300 font-mono">{rom.deviceId}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-neutral-300">{rom.workspaceId}</span>
                    </TableCell>
                    <TableCell>{getStatusBadge(rom.status)}</TableCell>
                    <TableCell className="text-sm text-neutral-300">
                      {formatDistanceToNow(new Date(rom.createdAt), {
                        addSuffix: true,
                        locale: es,
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      {rom.status === "active" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(rom.id);
                          }}
                          disabled={isBusy}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          Eliminar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {isExpanded && <ParticipantsRow romId={rom.id} />}
                </>
              );
            })}
          </TableBody>
        </Table>

        {isBusy && <LoadingOverlay />}

        {!isLoading && filteredRoms.length === 0 && (
          <EmptyState
            title="Sin ROMs"
            description="No hay sesiones remotas registradas."
            className="m-6"
          />
        )}

        <div className="border-t border-neutral-800 px-4 py-3 text-xs text-neutral-400">
          Total: {filteredRoms.length} ROM{filteredRoms.length !== 1 ? "s" : ""}
        </div>
      </Card>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, romId: null })}
        onConfirm={confirmDelete}
        title="Eliminar ROM"
        message="¿Está seguro que desea marcar esta ROM como eliminada? El dispositivo remoto será desconectado."
        confirmText="Eliminar"
        confirmVariant="danger"
      />
    </div>
  );
};

export default RomsPage;
